import { getEncryptionKey } from "client/Api/FrontEndEncryption/frontEndEncryption";
import { Guid } from "client_legacy/ndaw/guid";
import { safeLocalStorage } from "client_legacy/ndaw/safeStorage";
import { waitForUserConfigurationToInitialize } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { StateStorage, StorageValue } from "client/zustand/notPublic";
import { decrypt, encrypt, SjclCipherEncrypted } from "sjcl";
import {
	MutedActivityFilterModel,
	NotificationViewModel
} from "client/Api/Notifications/notifications";
import {
	ActivityFeedStoreData,
	OrderedReadonlyArray,
	markAsOrdered,
	INTERPOLATED_UPDATE_ACTIVITY_VERSION,
	createActivitiesState
} from "./store";
import { IndexedDbStateStorage } from "client/zustand/persistence";

type PersistedModel = {
	encryptedActivities: OrderedReadonlyArray<SjclCipherEncrypted>;
	encryptedMuteFilters: SjclCipherEncrypted | undefined;
	encryptionPublic: string;
	version: number;
};

type FallbackStoragePersistedModel = PersistedModel & {
	environmentId: Guid;
	userId: number;
};

class FallbackStateStorage implements StateStorage {
	private readonly _serializer: EncryptionSerializer;
	constructor (serializer: EncryptionSerializer) {
		this._serializer = serializer;
	}

	public async getItem(name: string): Promise<string> {
		const {
			currentUserIdentity: {
				userId
			},
			databaseConfiguration: {
				environmentId
			}
		} = await waitForUserConfigurationToInitialize();

		const storedItem: FallbackStoragePersistedModel | nil = safeLocalStorage.getItem(this.getLocalStorageKey(name));
		if (!storedItem) {
			return "";
		}

		const {
			environmentId: persistedEnvironmentId,
			userId: persistedUserId,
			...expectedData
		} = storedItem;

		if (persistedEnvironmentId !== environmentId || persistedUserId !== userId) {
			this.clear(name);
			return "";
		}

		return this._serializer.serializePersistedModel(expectedData);
	}

	public async setItem(name: string, value: string): Promise<void> {
		const {
			currentUserIdentity: {
				userId
			},
			databaseConfiguration: {
				environmentId
			}
		} = await waitForUserConfigurationToInitialize();

		const model: PersistedModel = this._serializer.deserializePersistedModel(value);

		const MAX_AMOUNT_OF_ACTIVITIES_TO_PERSIST_IN_LOCAL_STORAGE = 100;

		const activitiesToPersist: SjclCipherEncrypted[] = model.encryptedActivities.slice(
			0,
			Math.min(
				MAX_AMOUNT_OF_ACTIVITIES_TO_PERSIST_IN_LOCAL_STORAGE,
				model.encryptedActivities.length
			)
		);

		const fallbackModel: FallbackStoragePersistedModel = {
			...model,
			encryptedActivities: markAsOrdered({alreadyOrderedItems: activitiesToPersist}),
			environmentId,
			userId
		};

		safeLocalStorage.setItem(this.getLocalStorageKey(name), fallbackModel);
	}

	public clear(name: string) {
		safeLocalStorage.removeItem(this.getLocalStorageKey(name));
	}

	private getLocalStorageKey(name: string): string {
		return `zustandStores:${name}`;
	}
}

export class SafeStateStorage implements StateStorage {
	private readonly _storage: Promise<StateStorage>;

	constructor(serializer: EncryptionSerializer) {
		const fallbackStorage = new FallbackStateStorage(serializer);
		const primaryStorage = new IndexedDbStateStorage({
			onAfterSetItem: (name) => fallbackStorage.clear(name),
			shouldOnlyPersistDataForCurrentUser: true
		});

		this._storage = primaryStorage.checkIfAvailable()
			.then(isAvailable => isAvailable ? primaryStorage : fallbackStorage);
	}

	public getItem(name: string): Promise<string | null> {
		return this._storage.then(
			storage => storage.getItem(name)
		);
	}

	public setItem(name: string, value: string): Promise<void> {
		return this._storage.then(
			storage => storage.setItem(name, value)
		);
	}
}

export class EncryptionSerializer {
	private _previousEncryptionKey: string | undefined;
	private _previousEncryptedData: Map<number, SjclCipherEncrypted>;

	constructor() {
		this._previousEncryptedData = new Map();
	}

	public async serializeState(
		value: StorageValue<ActivityFeedStoreData>
	): Promise<string> {
		const {
			state,
			version
		} = value;
		const {
			databaseConfiguration: {
				encryptionKey,
				encryptionPublic,
				maxAmountOfNotificationsToStoreInFrontEnd: maxAmountOfActivitiesToStore
			}
		} = await waitForUserConfigurationToInitialize();

		const activitiesToPersist: ReadonlyArray<NotificationViewModel> = state.allActivitiesIncludingHiddenToAUser
			.slice(0, Math.min(state.allActivitiesIncludingHiddenToAUser.length, maxAmountOfActivitiesToStore));

		const serializedActivities: { version: number; data: string; }[] = activitiesToPersist
			.map(c => ({ version: c.version, data: JSON.stringify(c) }));

		const encryptedActivities: SjclCipherEncrypted[] = [];
		const encryptedData: Map<number, SjclCipherEncrypted> = new Map();

		for (const serializedActivity of serializedActivities) {
			const encryptedActivity: SjclCipherEncrypted = this.encryptActivity(encryptionKey, serializedActivity);

			if (serializedActivity.version !== INTERPOLATED_UPDATE_ACTIVITY_VERSION) {
				encryptedData.set(serializedActivity.version, encryptedActivity);
			}
			encryptedActivities.push(encryptedActivity);
		}

		const serializedMuteFilters: string = JSON.stringify(state.muteFilters);
		const encryptedMuteFilters: SjclCipherEncrypted	= encrypt(encryptionKey, serializedMuteFilters);

		const model: PersistedModel = {
			encryptedActivities: markAsOrdered({alreadyOrderedItems: encryptedActivities}),
			encryptionPublic,
			encryptedMuteFilters,
			version
		};

		const serializedModel: string = this.serializePersistedModel(model);
		this._previousEncryptionKey = encryptionKey;
		this._previousEncryptedData = encryptedData;

		return serializedModel;
	}

	public async deserializeState(
		serializedModel: string
	): Promise<StorageValue<ActivityFeedStoreData>> {
		const {
			databaseConfiguration: {
				encryptionKey: currentEncryptionKey,
				encryptionPublic: currentEncryptionPublic
			}
		} = await waitForUserConfigurationToInitialize();

		const model: PersistedModel = this.deserializePersistedModel(serializedModel);
		const {
			encryptionPublic,
			encryptedActivities,
			encryptedMuteFilters,
			version
		} = model;

		const encryptionKey: string = encryptionPublic === currentEncryptionPublic
			? currentEncryptionKey
			: await getEncryptionKey(encryptionPublic);

		let muteFilters: ReadonlyArray<MutedActivityFilterModel>;
		if (encryptedMuteFilters) {
			const serializedMuteFilters: string = decrypt(encryptionKey, encryptedMuteFilters);
			muteFilters = JSON.parse(serializedMuteFilters);
		} else {
			muteFilters = [];
		}

		const allActivities: ReadonlyArray<NotificationViewModel> = encryptedActivities
			.map(a => decrypt(encryptionKey, a))
			.map(a => JSON.parse(a));

		const orderedActivities: OrderedReadonlyArray<NotificationViewModel> = markAsOrdered({alreadyOrderedItems: allActivities});

		const state: Partial<ActivityFeedStoreData> = {
			...createActivitiesState(orderedActivities, muteFilters),
			isInitialized: true
		};

		return {
			// NOTE: Type definitions for zustand require "deserialize" to return full state
			// But actually result of the deserialize method is passed to the zustand's set method,
			// which accepts partial state. If we pass entire state including placeholders for methods,
			// they will be overridden with these placeholders
			state: state as ActivityFeedStoreData,
			version
		};
	}

	public serializePersistedModel(model: PersistedModel): string {
		return JSON.stringify(model);
	}

	public deserializePersistedModel(str: string): PersistedModel {
		return JSON.parse(str);
	}

	private encryptActivity(encryptionKey: string, serializedActivity: { version: number; data: string; }): SjclCipherEncrypted {
		if (this._previousEncryptionKey === encryptionKey) {
			const cachedResult: SjclCipherEncrypted | undefined = this._previousEncryptedData.get(serializedActivity.version);

			if (cachedResult) {
				return cachedResult;
			}
		}

		return encrypt(encryptionKey, serializedActivity.data);
	}
}